
import { animate, style, transition, trigger } from '@angular/animations';
import { formatNumber } from '@angular/common';
import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';

import moment from 'moment-timezone';
import { Subscription, combineLatest, filter, map, switchMap } from 'rxjs';

import { gateCall } from '@Core/rxjs-utils';
import { DistributionQuotaInfo } from '../../../../../Common/Charts/models/distribution.quota.info';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { ActionKeys } from '../../../../../Common/Forms/enums/keys.enum';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { BuyQuotaDialogComponent } from '../../../../../Common/TargetingShared/components/BuyQuotaDialog/buy-quota-dialog.component';
import { TargetingPreferencesService } from '../../../../../Common/TargetingShared/services/targeting-preferences.service';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { RecipientInformationModel } from '../../../models/recipient-information.model';
import { RecipientEditorService } from '../RecipientEditor/recipient-editor.service';
import { ConfigService } from '@Core/Config/services/config.service';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';


@Component({
    selector: 'agpr-quota-dropdown',
    templateUrl: './quota-dropdown.component.html',
    styleUrls: ['./quota-dropdown.component.scss'],
    animations: [
        trigger('summaryToggle', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('100ms', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('100ms', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})

export class QuotaDropdownComponent implements OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    totalRecipients: number;
    recipientInformation: RecipientInformationModel;
    distributionQuotaInfo: DistributionQuotaInfo;
    quotaResetDate: string = '';
    recipientsAvailable: string = '';
    collapsed: boolean = true;
    remainingUsagePercentage: number;
    remainingUsageCount: number;
    subscriptions: Subscription[] = [];
    animating: boolean = false;
    showThirdPartyWarning: boolean = false;

    @Output() collapsedChange: EventEmitter<any> = new EventEmitter();
    @Output() beforeToggle: EventEmitter<any> = new EventEmitter();
    @Output() afterToggle: EventEmitter<any> = new EventEmitter();

    constructor(
        public coreDialogService: CoreDialogService,
        public recipientEditorService: RecipientEditorService,
        public targetingPreferencesService: TargetingPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private configService: ConfigService,
        private distributionService: DistributionService
    ) {
        this.subscriptions.push(
            this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x)).subscribe(recipientInfo => {
                this.recipientInformation = recipientInfo;
                this.totalRecipients = recipientInfo.total;
                this.updateQuotaNotice();
            }),
            this.targetingPreferencesService.distributionQuotaInfo$.pipe(filter(x => !!x)).subscribe(quota => {
                this.distributionQuotaInfo = quota;
                this.recipientsAvailable = formatNumber(this.distributionQuotaInfo.totalDistributionsAvailable, 'en');
                this.updateQuotaNotice();
            }),
            combineLatest([
                this.currentUserStateService.getPreferences(),
                this.targetingPreferencesService.getQuotaExpiryDate()
            ]).subscribe(([prefs, resetDate]) => {
                if (resetDate) {
                    this.quotaResetDate = moment(resetDate).format(prefs.getDateFormatString(true));
                }
            }),
            gateCall(this.currentUserStateService.getAccount().pipe(
                map(account => this.configService.accountHasAccessToThirdPartyPitchingOnEnvironment(account.id))
            )).pipe(
                switchMap(() => this.distributionService.getFocusedResource()),
            ).subscribe(distribution => {
                this.showThirdPartyWarning = !!distribution?.sendFrom;
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private updateQuotaNotice() {
        if (this.recipientInformation && this.distributionQuotaInfo) {
            this.calculateRemainingUsage();
            // this.buildProgressChart();
        }
    }

    openBuyQuotaDialog() {
        this.coreDialogService.open(BuyQuotaDialogComponent, {
            inputs: {
                confirmAction: (amount) => {
                    return this.targetingPreferencesService.buyQuota(amount);
                }
            },
            outputs: {}
        });
    }

    /* TODO: needs design for visualization - NEP-4509
    buildProgressChart() {
        this.quotaNoticeChart = new ProgressChartModel([{
            label: '',
            dataProvider: of(
                [
                    {
                        from: 0,
                        to: this.distributionQuotaInfo.usedDistributions / this.distributionQuotaInfo.totalDistributions * 100,
                        name: this.localizationService.transform('Discover.targeting.distribution.editRecipient.chart.used'),
                        fill: chartColors.agprLighterMediumGrey
                    },
                    {
                        from: Math.min(100, this.distributionQuotaInfo.usedDistributions / this.distributionQuotaInfo.totalDistributions * 100),
                        to: Math.min(100, (this.distributionQuotaInfo.usedDistributions + this.recipientInformation.total)
                            / this.distributionQuotaInfo.totalDistributions * 100),
                        name: this.localizationService.transform('Discover.targeting.distribution.editRecipient.chart.currentDistribution'),
                        fill: chartColors.agprDarkBlue
                    },
                    {
                        from: Math.min(100, (this.distributionQuotaInfo.usedDistributions + this.recipientInformation.total)
                            / this.distributionQuotaInfo.totalDistributions * 100),
                        to: 100,
                        name: this.localizationService.transform('Discover.targeting.distribution.editRecipient.chart.remaining'),
                        fill: chartColors.agprMediumBlue
                    },
                ]
            )
        }], null);
    }
    */

    private calculateRemainingUsage() {
        if (this.distributionQuotaInfo.totalDistributionsAvailable > 0) {
            this.remainingUsagePercentage = this.totalRecipients / this.distributionQuotaInfo.totalDistributionsAvailable * 100;
        } else {
            this.remainingUsagePercentage = 100;
        }
        this.remainingUsageCount = Math.max(0, this.distributionQuotaInfo.totalDistributionsAvailable - this.totalRecipients);
    }

    toggleRecipientSummary(event) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        if (this.animating) {
            return false;
        }

        this.animating = true;
        this.beforeToggle.emit({originalEvent: event, collapsed: this.collapsed});
        if (this.collapsed) {
            this.expand();
        } else {
            this.collapse();
        }
        event.preventDefault();
    }

    expand() {
        this.collapsed = false;
        this.collapsedChange.emit(this.collapsed);
    }

    collapse() {
        this.collapsed = true;
        this.collapsedChange.emit(this.collapsed);
    }

    onToggleDone(event: Event) {
        this.animating = false;
        this.afterToggle.emit({originalEvent: event, collapsed: this.collapsed});
    }
}
